import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, Save, ArrowUp, ArrowDown } from 'lucide-react';
import api, { resolveImg } from '../../api';
import { toast } from 'sonner';
import { Input } from './NewsManager';
import ImageUpload from './ImageUpload';

// Reusable dialog wrapper
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
    <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 md:p-8 w-full max-w-lg my-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">{title}</h3>
        <button onClick={onClose} className="text-white/60 hover:text-white"><X size={24} /></button>
      </div>
      {children}
    </div>
  </div>
);

// ============= TEAM SECTION =============
const TeamSection = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', role: '', photo: '', order: 0 });

  const load = () => api.get('/team').then((r) => {
    const sorted = [...r.data].sort((a, b) => (a.order || 0) - (b.order || 0));
    setItems(sorted);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === 'new') await api.post('/team', payload);
      else await api.put(`/team/${editing}`, payload);
      toast.success('Enregistré'); setEditing(null); load();
    } catch (e) { toast.error('Erreur'); }
  };
  const remove = async (id) => { if (!window.confirm('Supprimer ?')) return; await api.delete(`/team/${id}`); toast.success('Supprimé'); load(); };
  const move = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= items.length) return;
    const reordered = [...items];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(j, 0, moved);
    setItems(reordered);
    try {
      await Promise.all(reordered.map((it, i) => api.put(`/team/${it.id}`, { order: i })));
      load();
    } catch (e) { toast.error('Erreur'); load(); }
  };

  return (
    <div className="mb-12" data-testid="team-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">Membres du Bureau</h3>
        <button onClick={() => { setForm({ name: '', role: '', photo: '', order: items.length }); setEditing('new'); }} className="flex items-center gap-2 px-3 py-2 bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-lg text-fuchsia-300 text-sm" data-testid="add-team-btn">
          <Plus size={16} /> Ajouter
        </button>
      </div>
      <div className="grid gap-3">
        {items.map((m, i) => (
          <div key={m.id} className="min-w-0 flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-white/5 border border-white/10 flex-shrink-0">
              {m.photo ? <img src={resolveImg(m.photo)} alt={m.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-fuchsia-400 font-bold">{m.name?.charAt(0)}</div>}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-medium truncate">{m.name}</h4>
              <p className="text-white/50 text-sm truncate">{m.role}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-white/60 hover:text-fuchsia-400 disabled:opacity-30" title="Monter"><ArrowUp size={16} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 text-white/60 hover:text-fuchsia-400 disabled:opacity-30" title="Descendre"><ArrowDown size={16} /></button>
              <button onClick={() => { setForm({ ...m }); setEditing(m.id); }} className="p-2 text-white/60 hover:text-fuchsia-400" data-testid={`edit-team-${m.id}`}><Edit2 size={16} /></button>
              <button onClick={() => remove(m.id)} className="p-2 text-white/60 hover:text-red-400" data-testid={`delete-team-${m.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title="Membre" onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Input label="Nom" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Input label="Rôle" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
            <ImageUpload value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} label="Photo du membre" />
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium" data-testid="save-team-btn"><Save size={18} /> Enregistrer</button>
            <button onClick={() => setEditing(null)} className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-white/70">Annuler</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ============= TRAINING GROUPS SECTION =============
const GroupsSection = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', ageRange: '', description: '', schedule: '', locations: [], order: 0 });

  const load = () => api.get('/training-groups').then((r) => {
    const sorted = [...r.data].sort((a, b) => (a.order || 0) - (b.order || 0));
    setItems(sorted);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, locations: (form.locations || []).filter(Boolean), order: Number(form.order) || 0 };
      if (editing === 'new') await api.post('/training-groups', payload);
      else await api.put(`/training-groups/${editing}`, payload);
      toast.success('Enregistré'); setEditing(null); load();
    } catch (e) { toast.error('Erreur'); }
  };
  const remove = async (id) => { if (!window.confirm('Supprimer ?')) return; await api.delete(`/training-groups/${id}`); toast.success('Supprimé'); load(); };
  const move = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= items.length) return;
    const reordered = [...items];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(j, 0, moved);
    setItems(reordered);
    try {
      await Promise.all(reordered.map((it, i) => api.put(`/training-groups/${it.id}`, { order: i })));
      load();
    } catch (e) { toast.error('Erreur'); load(); }
  };

  const updateLoc = (i, v) => { const l = [...(form.locations || [])]; l[i] = v; setForm({ ...form, locations: l }); };
  const addLoc = () => setForm({ ...form, locations: [...(form.locations || []), ''] });
  const removeLoc = (i) => setForm({ ...form, locations: (form.locations || []).filter((_, idx) => idx !== i) });

  return (
    <div className="mb-12" data-testid="groups-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">Groupes d'entraînement</h3>
        <button onClick={() => { setForm({ name: '', ageRange: '', description: '', schedule: '', locations: [], order: items.length }); setEditing('new'); }} className="flex items-center gap-2 px-3 py-2 bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-lg text-fuchsia-300 text-sm" data-testid="add-group-btn">
          <Plus size={16} /> Ajouter
        </button>
      </div>
      <div className="grid gap-3">
        {items.map((g, i) => (
          <div key={g.id} className="min-w-0 flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-medium truncate">{g.name} <span className="text-fuchsia-400 text-sm">• {g.ageRange}</span></h4>
              <p className="text-white/50 text-sm truncate">{g.schedule}{g.locations?.length ? ` • ${g.locations.join(', ')}` : ''}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-white/60 hover:text-fuchsia-400 disabled:opacity-30" data-testid={`group-up-${g.id}`}><ArrowUp size={16} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 text-white/60 hover:text-fuchsia-400 disabled:opacity-30" data-testid={`group-down-${g.id}`}><ArrowDown size={16} /></button>
              <button onClick={() => { setForm({ ...g, locations: g.locations || [] }); setEditing(g.id); }} className="p-2 text-white/60 hover:text-fuchsia-400" data-testid={`edit-group-${g.id}`}><Edit2 size={16} /></button>
              <button onClick={() => remove(g.id)} className="p-2 text-white/60 hover:text-red-400" data-testid={`delete-group-${g.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title="Groupe d'entraînement" onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Input label="Nom du groupe" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Input label="Tranche d'âge" value={form.ageRange} onChange={(v) => setForm({ ...form, ageRange: v })} />
            <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea />
            <Input label="Horaires" value={form.schedule} onChange={(v) => setForm({ ...form, schedule: v })} textarea />
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-white/70 text-sm">Lieux d'entraînement</label>
                <button type="button" onClick={addLoc} className="text-fuchsia-400 text-sm flex items-center gap-1"><Plus size={14} /> Ajouter</button>
              </div>
              {(form.locations || []).map((l, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={l} onChange={(e) => updateLoc(i, e.target.value)} placeholder="Ex: Gymnase La Saline" className="flex-1 bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white text-sm" />
                  <button type="button" onClick={() => removeLoc(i)} className="p-2 text-white/60 hover:text-red-400"><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium" data-testid="save-group-btn"><Save size={18} /> Enregistrer</button>
            <button onClick={() => setEditing(null)} className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-white/70">Annuler</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ============= LOCATIONS SECTION =============
const LocationsSection = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '', website: '', isMain: false });

  const load = () => api.get('/training-locations').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === 'new') await api.post('/training-locations', form);
      else await api.put(`/training-locations/${editing}`, form);
      toast.success('Enregistré'); setEditing(null); load();
    } catch (e) { toast.error('Erreur'); }
  };
  const remove = async (id) => { if (!window.confirm('Supprimer ?')) return; await api.delete(`/training-locations/${id}`); toast.success('Supprimé'); load(); };

  return (
    <div className="mb-12" data-testid="locations-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">Lieux d'entraînement</h3>
        <button onClick={() => { setForm({ name: '', address: '', phone: '', website: '', isMain: false }); setEditing('new'); }} className="flex items-center gap-2 px-3 py-2 bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-lg text-fuchsia-300 text-sm" data-testid="add-location-btn">
          <Plus size={16} /> Ajouter
        </button>
      </div>
      <div className="grid gap-3">
        {items.map((l) => (
          <div key={l.id} className="min-w-0 flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-medium truncate">{l.name} {l.isMain && <span className="ml-2 px-2 py-0.5 bg-fuchsia-500 text-white text-xs rounded">PRINCIPAL</span>}</h4>
              <p className="text-white/50 text-sm truncate">{l.address}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => { setForm({ ...l }); setEditing(l.id); }} className="p-2 text-white/60 hover:text-fuchsia-400" data-testid={`edit-location-${l.id}`}><Edit2 size={16} /></button>
              <button onClick={() => remove(l.id)} className="p-2 text-white/60 hover:text-red-400" data-testid={`delete-location-${l.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title="Lieu d'entraînement" onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Input label="Nom du lieu" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Input label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            <Input label="Téléphone (optionnel)" value={form.phone || ''} onChange={(v) => setForm({ ...form, phone: v })} />
            <Input label="Site web (optionnel)" value={form.website || ''} onChange={(v) => setForm({ ...form, website: v })} />
            <label className="flex items-center gap-3 text-white/70">
              <input type="checkbox" checked={!!form.isMain} onChange={(e) => setForm({ ...form, isMain: e.target.checked })} className="w-4 h-4" />
              Lieu principal
            </label>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium" data-testid="save-location-btn"><Save size={18} /> Enregistrer</button>
            <button onClick={() => setEditing(null)} className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-white/70">Annuler</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const ClubManager = () => (
  <div data-testid="club-manager">
    <h2 className="text-2xl font-bold text-white mb-8">Notre Club</h2>
    <TeamSection />
    <GroupsSection />
    <LocationsSection />
  </div>
);

export default ClubManager;
